import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PaymentLayoutActions, PaymentLayoutActionTypes } from './../actions/payment-layout.action';
import { CoreService } from './../../../core/services/core.service';
import { withLatestFrom, switchMap, filter } from 'rxjs/operators';
import { Customer } from './../../models/customer';
import { Company } from './../../models/company';
import { Jobsite } from './../../models/jobsite';
import { from } from 'rxjs';
import { Store, select } from '@ngrx/store';
import * as fromPayments from './../reducers/index';
import { PaymentConfigurationActions } from '../actions/payment-configuration.action';
import * as RouterActions from '@core/actions/router.action';
import { AppConfigurationService } from '../../../../cemexng2components/components/app-configuration';

@Injectable()
export class PaymentLayoutEffect {

    constructor(
        protected actions$: Actions,
        protected store$: Store<fromPayments.State>,
        protected coreService: CoreService,
        protected features: AppConfigurationService
    ) { }

    loadCustomers$  = createEffect(() => this.actions$.pipe(
        ofType<PaymentLayoutActions.LoadCustomers>(PaymentLayoutActionTypes.LOAD_CUSTOMERS),
        withLatestFrom(this.coreService.getLegalEntityCustomers()),
        switchMap(([action, customers]) => {
            const customersToState: Customer[] = [];
            customers.forEach(customer => {
                customersToState.push({
                    customerCode: customer.customerCode,
                    customerDesc: customer.customerDesc,
                    countryCode: customer.countryCode.trim()
                });
            });

            return from([
                new PaymentLayoutActions.ClearCustomers(),
                new PaymentLayoutActions.LoadCustomersSuccess(customersToState)
            ]);
        })
    ));

    loadCompanies$  = createEffect(() => this.actions$.pipe(
        ofType<PaymentLayoutActions.LoadCompanies>(PaymentLayoutActionTypes.LOAD_COMPANIES),
        withLatestFrom(this.store$.pipe(select(fromPayments.getSelectedCustomer))),
        switchMap(([action, customer]) => {
            return this.coreService.getLegalEntityByCustomer(customer.customerCode, customer.countryCode).pipe(
                switchMap((companies: any) => {
                    const companiesToState: Company[] = [];
                    companies = companies.filter(company => company.paymentAllowed === 1);

                  if (this.features.isEnabled("dashboard.pagosenlinea-enabled")) {
                      const dominio = this.features.getConfiguration('dashboard.pagosenlinea-enabled').options.dominio;
                      const companiesInclude = this.features.getConfiguration('dashboard.pagosenlinea-enabled').options.companies;
                      let filteredCompanies: any[] = [];

                      if (window.location.hostname.includes(dominio)) {
                          filteredCompanies = companies.filter(company => companiesInclude.includes(company.companyCode));
                          return from([
                              new PaymentLayoutActions.ClearCompanies(),
                              new PaymentLayoutActions.LoadCompaniesSuccess(filteredCompanies)
                          ]);
                      }
                    }

                    companies.forEach(company => {
                          if (company.applications && company.applications.length > 0) {
                            company.applicationCode = company.applications[0].applicationCode;
                          }

                          companiesToState.push({
                              companyCode: company.companyCode,
                              companyDesc: company.companyDesc,
                              countryCode: company.countryCode.trim(),
                              divisionCode: company.divisionCode ? company.divisionCode : 0,
                              paymentAllowed: company.paymentAllowed,
                              applicationCode: company.applicationCode
                          });
                      });

                    return from([
                        new PaymentLayoutActions.ClearCompanies(),
                        new PaymentLayoutActions.LoadCompaniesSuccess(companiesToState)
                    ]);
                })
            );
        })
    ));

    loadJobsites$  = createEffect(() => this.actions$.pipe(
        ofType<PaymentLayoutActions.LoadJobsites>(PaymentLayoutActionTypes.LOAD_JOBSITES),
        withLatestFrom(
            this.store$.pipe(select(fromPayments.getSelectedCustomer)),
            this.store$.pipe(select(fromPayments.getSelectedCompany))
        ),
        filter(([action, customer, company]) => customer !== undefined && company !== undefined && customer !== null && company !== null),
        switchMap(([action, customer, company]) => {
            return this.coreService.getLegalEntityJobsites(customer.customerCode, company.companyCode, customer.countryCode).pipe(
                switchMap(jobsites => {
                    const jobsitesToState: Jobsite[] = [];
                    jobsites.forEach(jobsite => {
                        jobsitesToState.push({
                            jobsiteCode: jobsite.jobSiteCode,
                            jobsiteDesc: jobsite.jobSiteDesc
                        });
                    });

                    return from([
                        new PaymentLayoutActions.ClearJobsites(),
                        new PaymentLayoutActions.LoadJobsitesSuccess(jobsitesToState)
                    ]);
                })
            );
        })
    ));

    exitPaymentsPage$ = createEffect(() =>this.actions$.pipe(
        ofType<PaymentLayoutActions.ExitPaymentsPage>(PaymentLayoutActionTypes.EXIT_PAYMENTS_PAGE),
        switchMap(() =>
            from([
              new PaymentLayoutActions.ClearCustomers(),
              new PaymentLayoutActions.ClearCompanies(),
              new PaymentLayoutActions.ClearJobsites(),
              new PaymentConfigurationActions.ClearPaymentConfiguration(),
              new RouterActions.RouterBack()
            ])
        )
    ));
}
